import React, { useState, useEffect, useRef } from "react";
import {
	addProductToCommunity,
	updateProductFromCommunity,
	sendCommunityUpdate
} from "../../../../Service/user";
import { getBase64 } from "../../../../utils/getBase64";
import { toast } from "react-toastify";
import "./AddProductPopup.scss";
import SampleProduct from "../../../../Images/bg-product-comm.jpg";
import ReactQuill from 'react-quill';
import 'react-quill/dist/quill.snow.css';

const AddProductPopup = ({
	communityId,
	onClose,
	onProductAdded,
	onProductUpdate,
	product,
}) => {
	const initialFormData = {
		name: "",
		description: "",
		is_free: true,
		amount: "",
		image: null,
		URLS: [""],
	};

	const [formData, setFormData] = useState(initialFormData);

	useEffect(() => {
		if (product) {
			setFormData({
				name: product.name || initialFormData.name,
				description: product.description || initialFormData.description,
				is_free: product.is_free ?? initialFormData.is_free,
				amount: product.amount || initialFormData.amount,
				image: product.image || initialFormData.image,
				URLS: product.URLS || initialFormData.URLS,
			});
		} else {
			setFormData(initialFormData);
		}
	}, [product]);

	const [isLoading, setIsLoading] = useState(false);
	const editorRef = useRef(null);

	const modules = {
		toolbar: [
			[{ header: [1, 2, 3, false] }],
			['bold', 'italic', 'underline', 'strike'],
			[{ list: 'ordered' }, { list: 'bullet' }],
			['link'],
			['clean']
		]
	};

	const formats = [
		'header',
		'bold', 'italic', 'underline', 'strike',
		'list', 'bullet',
		'link'
	];

	const handleChange = (e) => {
		const { name, value, type, checked } = e.target;
		setFormData((prev) => ({
			...prev,
			[name]: type === "checkbox" ? checked : value,
		}));
	};

	const handleUrlChange = (index, value) => {
		const newUrls = [...formData.URLS];
		newUrls[index] = value;
		setFormData((prev) => ({ ...prev, URLS: newUrls }));
	};

	const addUrlField = () => {
		if (formData.URLS.length < 5) {  // Add URL only if less than 5
			setFormData((prev) => ({ ...prev, URLS: [...prev.URLS, ""] }));
		} else {
			toast.warning("Maximum 5 URLs allowed");
		}
	};

	const removeUrlField = (index) => {
		setFormData((prev) => ({
			...prev,
			URLS: prev.URLS.filter((_, i) => i !== index),
		}));
	};

	const handleFileChange = async (e) => {
		const file = e.target.files[0];
		if (file) {
			const base64 = await getBase64(file);
			setFormData((prev) => ({ ...prev, image: base64 }));
		}
	};

	const resetForm = () => {
		setFormData(initialFormData);
	};

	const convertImageToBase64 = async (imageUrl) => {
		try {
			// Create a new image element
			const img = new Image();
			img.src = SampleProduct;
			
			// Wait for the image to load
			await new Promise((resolve) => {
				img.onload = resolve;
			});

			// Create canvas and convert to base64
			const canvas = document.createElement('canvas');
			canvas.width = img.width;
			canvas.height = img.height;
			const ctx = canvas.getContext('2d');
			ctx.drawImage(img, 0, 0);
			
			// Convert to base64 string (remove the data:image/jpeg;base64, prefix)
			const base64String = canvas.toDataURL('image/jpeg');
			return base64String;
		} catch (error) {
			console.error('Error converting image to base64:', error);
			return null;
		}
	};

	const handleSubmit = async (e) => {
		e.preventDefault();
		setIsLoading(true);

		try {
			let imageToUse = formData.image;
			
			// If no image is selected, convert SampleProduct to base64
			if (!imageToUse) {
				imageToUse = await convertImageToBase64(SampleProduct);
			}

			const dataToSubmit = {
				...formData,
				image: imageToUse
			};

			if (product) {
				const data = await updateProductFromCommunity(communityId, product._id, dataToSubmit);
				
				// Send update notification for product updates
				await sendCommunityUpdate({
					communityId: communityId,
					updateType: "Product",
					description: `Product "${dataToSubmit.name}" has been updated in the community.`
				});
				
				onProductUpdate(data.data);
			} else {
				const data = await addProductToCommunity(communityId, dataToSubmit);
				
				// Send update notification for new products
				await sendCommunityUpdate({
					communityId: communityId,
					updateType: "Product",
					description: `A new product "${dataToSubmit.name}" has been added to the community.`
				});
				
				onProductAdded(data.data);
			}
			
			resetForm();
			onClose();
		} catch (error) {
			console.error('Error submitting product:', error);
			toast.error("Failed to submit product");
		} finally {
			setIsLoading(false);
		}
	};

	return (
		<div className="add-product-popup">
			<div className="add-product-popup-content">
				<div className="add-product-popup-content-left">
					<div className="image-selector-label">Choose an image for your product</div>
					<label className="image-selector">
						<input
							type="file"
							onChange={handleFileChange}
							accept="image/*"
							hidden
						/>
						<img
							src={formData.image || SampleProduct}
							alt="Product Preview"
							className="product-image-preview"
						/>
					</label>
				</div>
				<div className="add-product-popup-content-right">
					<button className="add-product-close-btn" onClick={onClose}>
						×
					</button>
					<h2>{product ? "Update Product" : "New Product"}</h2>
					<form onSubmit={handleSubmit}>
						<div className="add-product-form-group">
							<label>Product Name</label>
							<input
								type="text"
								name="name"
								value={formData.name}
								onChange={handleChange}
								placeholder="Enter product name"
								required
							/>
						</div>

						<div className="add-product-form-group">
							<label>Description</label>
							<ReactQuill
								ref={editorRef}
								theme="snow"
								value={formData.description}
								onChange={(value) => setFormData({...formData, description: value})}
								modules={modules}
								formats={formats}
								placeholder="Enter product description"
								className="custom-quill-product"
							/>
						</div>

						<div className="add-product-form-group add-product-checkbox">
							<label>
								<input
									type="checkbox"
									name="is_free"
									checked={formData.is_free}
									onChange={handleChange}
								/>
								Free Resource
							</label>
						</div>

						{!formData.is_free && (
							<div className="add-product-form-group">
								<label>Amount</label>
								<input
									type="number"
									name="amount"
									value={formData.amount}
									onChange={handleChange}
									required
									min="0"
								/>
							</div>
						)}

						<div className="add-product-form-group">
							<label>Resource URLs <span className="url-counter">{`(${5 - formData.URLS.length} remaining)`}</span></label>
							{formData.URLS.map((url, index) => (
								<div key={index} className="add-product-url-input">
									<input
										type="url"
										value={url}
										onChange={(e) => handleUrlChange(index, e.target.value)}
										placeholder="Enter resource URL"
									/>
									<button
										type="button"
										onClick={() => removeUrlField(index)}
										className="add-product-remove-url"
									>
										×
									</button>
								</div>
							))}
							{formData.URLS.length < 5 && (
								<button
									type="button"
									onClick={addUrlField}
									className="add-product-add-url"
								>
									+ Add Another URL
								</button>
							)}
						</div>

						<div className="add-product-button-group">
							<button
								type="button"
								onClick={onClose}
								className="add-product-cancel-btn"
							>
								Cancel
							</button>
							<button
								type="submit"
								className="add-product-submit-btn"
								disabled={isLoading}
							>
								{isLoading
									? product
										? "Updating..."
										: "Adding..."
									: product
									? "Update Product"
									: "Add Product"}
							</button>
						</div>
					</form>
				</div>
			</div>
		</div>
	);
};

export default AddProductPopup;
